import { produce } from 'immer';
import { LOCATION_CHANGE, LocationChangeAction } from 'react-router-redux';

import { getSheetKey } from './sheetSelectors';

import { SheetAction, SheetActionTypes } from './sheetActionTypes';
import { SheetFlowId, SheetReducerState, SheetState } from './sheetTypes';

const INITIAL_STATE: SheetReducerState = {};
type SheetEntry = [string, SheetState];

/**
 * Find the currently active sheet in a flow, excluding the current sheet if provided.
 */
const getActiveFlowSheetEntry = (
    state: SheetReducerState,
    flow?: SheetFlowId,
    excludedSheetKey?: string,
): SheetEntry | null => {
    if (!flow) return null;

    const sheetKeys = Object.keys(state);
    if (sheetKeys.length === 0) return null;

    for (const sheetKey of sheetKeys) {
        if (sheetKey === excludedSheetKey) continue;

        const sheetState = state[sheetKey];
        if (!sheetState?.active) continue;
        if (sheetState.config?.flow !== flow) continue;

        return [sheetKey, sheetState];
    }

    return null;
};

export default (state = INITIAL_STATE, action: SheetAction | LocationChangeAction) => {
    switch (action.type) {
        case SheetActionTypes.SHEET_OPEN:
            return produce(state, (draft) => {
                const { sheetId, instanceId, options, config } = action;

                const sheetKey = getSheetKey(sheetId, instanceId);
                if (state[sheetKey]?.active === true) return;

                if (!sheetId) return;

                const activeFlowSheetEntry = getActiveFlowSheetEntry(state, config?.flow, sheetKey);
                const previousSheetState = options?.previousSheetState || activeFlowSheetEntry?.[1];

                if (activeFlowSheetEntry) {
                    const [activeFlowSheetKey] = activeFlowSheetEntry;
                    delete draft[activeFlowSheetKey];
                }

                draft[sheetKey] = {
                    active: true,
                    sheetId,
                    instanceId,
                    activeSnapPoint: 0,
                    previousSheetState,
                    config,
                };
            });
        case SheetActionTypes.SHEET_CLOSE:
            return produce(state, (draft) => {
                const { sheetId, instanceId, options } = action;

                const sheetKey = getSheetKey(sheetId, instanceId);
                const sheetState = state[sheetKey];

                if (sheetState?.previousSheetState && !options?.skipPreviousSheetRestore) {
                    const previousSheetKey = getSheetKey(
                        sheetState.previousSheetState.sheetId,
                        sheetState.previousSheetState.instanceId,
                    );
                    draft[previousSheetKey] = {
                        ...sheetState.previousSheetState,
                        activeSnapPoint: 0,
                    };
                }

                delete draft[sheetKey];
            });
        case SheetActionTypes.SHEET_UPDATE_ACTIVE_SNAP_POINT:
            return produce(state, (draft) => {
                const { sheetId, instanceId } = action;

                const sheetKey = getSheetKey(sheetId, instanceId);
                const sheet = draft[sheetKey];
                if (!sheet) return;

                sheet.activeSnapPoint = action.activeSnapPoint;
            });
        case LOCATION_CHANGE:
            return produce(state, (draft) => {
                Object.keys(draft).forEach((sheetKey) => {
                    const sheet = draft[sheetKey];
                    if (!sheet?.config?.stayActiveOnLocationChange) {
                        delete draft[sheetKey];
                    }
                });
            });
        default:
            return state;
    }
};
